import { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import { Upload, Loader2, Globe, TrendingUp, Store, CheckCircle, XCircle } from 'lucide-react';

interface CompetitorResult {
  domain: string;
  dr: number | null;
  ref_domains: number | null;
  backlinks: number | null;
  min_price: number | null;
  max_price: number | null;
  avg_price: number | null;
  seller_count: number;
  found_in_db: boolean;
}

interface AnalysisResponse {
  total_domains_uploaded: number;
  matched_domains: number;
  unmatched_domains: number;
  total_estimated_spend: number;
  avg_price_per_link: number;
  results: CompetitorResult[];
  top_sellers: Array<{ name: string; domain_count: number }>;
}

export default function CompetitorAnalysis() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);

  const handleUpload = async (file: File) => {
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/api/competitor/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalysis(res.data);
    } catch (err: any) {
      setError(getApiError(err, 'Analysis failed'));
    } finally {
      setLoading(false);
    }
  };

  const formatNum = (n: number | null) => {
    if (n === null) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Competitor Backlink Analysis</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Upload your competitor's Ahrefs backlink export to see where they bought links and how much they paid
          </p>
        </div>

        {!analysis && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <Loader2 className="w-10 h-10 animate-spin text-brand-500" />
                <p className="text-sm text-gray-500">Analyzing backlinks...</p>
              </div>
            ) : (
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Upload Ahrefs Backlink CSV</h3>
                <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                  Export your competitor's backlinks from Ahrefs (Referring Domains report) and upload the CSV here. We'll match them against our pricing database.
                </p>
                <label className="inline-flex items-center px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg cursor-pointer">
                  Choose CSV File
                  <input type="file" accept=".csv" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} className="hidden" />
                </label>
              </div>
            )}
            {error && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          </div>
        )}

        {analysis && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Domains Uploaded', value: analysis.total_domains_uploaded, icon: Globe },
                { label: 'Matched in DB', value: analysis.matched_domains, icon: CheckCircle },
                { label: 'Not Found', value: analysis.unmatched_domains, icon: XCircle },
                { label: 'Est. Total Spend', value: `$${formatNum(analysis.total_estimated_spend)}`, icon: TrendingUp },
                { label: 'Avg Price/Link', value: `$${analysis.avg_price_per_link.toFixed(0)}`, icon: Store },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <Icon className="w-5 h-5 text-brand-500 mb-2" />
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Top Sellers */}
            {analysis.top_sellers.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Top Sellers Used by Competitor</h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.top_sellers.map(s => (
                    <span key={s.name} className="px-3 py-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-lg text-sm font-medium">
                      {s.name} ({s.domain_count})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Results Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DR</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sellers</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {analysis.results.map(r => (
                      <tr key={r.domain} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.domain}</td>
                        <td className="px-4 py-3">
                          {r.found_in_db ? (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">Found</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">Not in DB</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.dr ?? '—'}</td>
                        <td className="px-4 py-3">{r.avg_price ? <span className="font-semibold text-green-600">${r.avg_price.toFixed(0)}</span> : '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.found_in_db ? r.seller_count : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <button onClick={() => setAnalysis(null)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg">
              Analyze Another Competitor
            </button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
